import { useCallback } from 'react'
import restClient from 'Shared/hooks/restClient'
import { STATUS_LABELS, STATUS_CLASS, ENTITY_CONFIG, DEFAULT_VISIBLE } from './constants'
import './PinnedBookmarks.scoped.scss'

function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

function StarButton({ onRemove }) {
  return (
    <button
      type="button"
      className="star-btn"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove() }}
      aria-pressed
      aria-label="Click to remove from favourites"
    >
      <span className="star-icon"><SvgIconsBookmarkStar solid /></span>
      <svg className="star-x" width="14" height="14" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    </button>
  )
}

function StatusPill({ status }) {
  if (!status) return null
  return (
    <span className={`status-pill ${STATUS_CLASS[status] || 'draft'}`}>
      <span className="dot" />
      {STATUS_LABELS[status] || status}
    </span>
  )
}

function TypeIcon({ config }) {
  const { Icon } = config
  return (
    <span className="type-icon" aria-hidden>
      <Icon />
    </span>
  )
}

export default function PinnedBookmarks({ entityType, section }) {
  const [bookmarks, setBookmarks] = useState([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(false)

  const config = ENTITY_CONFIG[entityType]

  const fetchBookmarks = useCallback(() => {
    const params = { entity_type: entityType }
    if (section) params.section = section
    restClient.get('/api/user_bookmarks', { params })
      .then((r) => {
        setBookmarks(r.data)
        setLoading(false)
      })
      .catch((e) => {
        console.error(e)
        setLoading(false)
      })
  }, [entityType, section])

  useEffect(() => { fetchBookmarks() }, [fetchBookmarks])

  const selfEvent = useRef(false)

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.entityType !== entityType) return
      // Filter by section so a Pages widget doesn't refetch when a Ballot is
      // toggled (and vice versa). Events that omit section (e.g. Campaign,
      // Dashboard) always pass.
      if (section && e.detail?.section && e.detail.section !== section) return
      if (selfEvent.current) {
        selfEvent.current = false
        return
      }
      fetchBookmarks()
    }
    window.addEventListener('bookmarks-changed', handler)
    return () => window.removeEventListener('bookmarks-changed', handler)
  }, [entityType, section, fetchBookmarks])

  const handleRemove = (id) => {
    const removed = bookmarks.find((bookmark) => bookmark.id === id)
    setBookmarks((prev) => prev.filter((bookmark) => bookmark.id !== id))
    restClient.post('/api/user_bookmarks/toggle', { entity_type: entityType, entity_id: id })
      .then(() => {
        selfEvent.current = true
        const detail = { entityType }
        if (removed?.section) detail.section = removed.section
        window.dispatchEvent(new CustomEvent('bookmarks-changed', { detail }))
      })
  }

  if (loading || !config) return null

  if (bookmarks.length === 0) {
    return (
      <div className="pinned-bookmarks">
        <div className="pinned-empty">
          <span className="pinned-empty-title">No favourites yet</span>
          <span className="pinned-empty-help">
            Tap the <SvgIconsBookmarkStar width={14} height={14} /> on any {config.getLabel({ section })} below, to favourite it here.
          </span>
        </div>
      </div>
    )
  }

  const shown = expanded ? bookmarks : bookmarks.slice(0, DEFAULT_VISIBLE)
  const overflow = bookmarks.length - DEFAULT_VISIBLE

  return (
    <div className="pinned-bookmarks">
      <div className="pinned-header">
        <h6 className="section-label">
          Favourites · {bookmarks.length}
        </h6>
        {overflow > 0 && (
          <button
            type="button"
            className={`expand-toggle ${expanded ? 'is-open' : ''}`}
            onClick={() => setExpanded((o) => !o)}
          >
            {expanded ? 'Show less' : `+ ${overflow} more`}
            <Chevron />
          </button>
        )}
      </div>
      <div className="pinned-cards">
        {shown.map((bookmark) => {
          const metric = config.getMetric(bookmark)
          const status = config.getStatus(bookmark)
          const url = config.getUrl(bookmark)

          return (
            <a href={url} className="pinned-card" key={bookmark.id}>
              <TypeIcon config={config} />
              <div className="body">
                <span className="name">{bookmark.name}</span>
                <span className="meta">
                  {metric && (
                    <span className="stat-inline">
                      {metric.n && <span className="num">{metric.n}</span>}
                      <span className="lbl">{metric.lbl}</span>
                    </span>
                  )}
                  {status && metric && <span className="sep" />}
                  {status && <StatusPill status={status} />}
                </span>
              </div>
              <span className="card-star">
                <StarButton onRemove={() => handleRemove(bookmark.id)} />
              </span>
            </a>
          )
        })}
      </div>
    </div>
  )
}
